/**
 * <bt-ship-date>: cambia «sale el día 20» por la fecha exacta del próximo envío («20 de noviembre»).
 *
 * Reglas en data-*: día de corte, día de envío, meses especiales («mes:corte:envío, …») y fecha de
 * apertura (AAAA-MM-DD). Se calcula con la fecha de Madrid, así no depende de la zona del navegador
 * ni de una página servida desde caché. Si el envío cae en domingo, pasa al lunes.
 * Es un Component para recalcular también cuando el carrito lateral se vuelve a pintar (morph).
 */
import { Component } from '@theme/component';

const ZONA = 'Europe/Madrid';

/** @returns {{ y: number, m: number, d: number }} Fecha de hoy en Madrid (mes 1–12). */
function hoyEnMadrid() {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const valor = (tipo) => Number(partes.find((p) => p.type === tipo)?.value);
  return { y: valor('year'), m: valor('month'), d: valor('day') };
}

/**
 * @param {string} texto - «12:10:14, 2:1:5»
 * @returns {Map<number, { corte: number, envio: number }>}
 */
function leerExcepciones(texto) {
  const mapa = new Map();
  for (const trozo of (texto || '').split(',')) {
    const [mes, corte, envio] = trozo.trim().split(':').map(Number);
    const valido = [mes, corte, envio].every(Number.isInteger) && mes >= 1 && mes <= 12;
    if (valido && corte >= 1 && envio >= 1 && envio <= 28) mapa.set(mes, { corte, envio });
  }
  return mapa;
}

/**
 * @typedef {{ text: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class BtShipDate extends Component {
  requiredRefs = ['text'];

  connectedCallback() {
    super.connectedCallback();
    this.#pintar();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#pintar();
  }

  #pintar() {
    const texto = this.refs.text;
    const plantilla = this.dataset.template;
    if (!plantilla?.includes('[fecha]')) return;

    const fecha = this.#proximoEnvio();
    if (!fecha) return;

    const idioma = document.documentElement.lang || 'es';
    const legible = new Intl.DateTimeFormat(idioma, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(fecha);
    texto.textContent = plantilla.replace('[fecha]', legible);
  }

  /** @returns {Date | null} Fecha del próximo envío (en UTC, solo día). */
  #proximoEnvio() {
    const corteBase = Number(this.dataset.cutoff);
    const envioBase = Number(this.dataset.ship);
    if (!Number.isInteger(corteBase) || !Number.isInteger(envioBase)) return null;

    const excepciones = leerExcepciones(this.dataset.exceptions);
    const reglas = (mes) => excepciones.get(mes) ?? { corte: corteBase, envio: envioBase };

    // Antes de la apertura se cuenta desde el día de apertura.
    let { y, m, d } = hoyEnMadrid();
    const apertura = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.dataset.launch || '');
    if (apertura) {
      const [ay, am, ad] = apertura.slice(1).map(Number);
      if (ay * 10000 + am * 100 + ad > y * 10000 + m * 100 + d) [y, m, d] = [ay, am, ad];
    }

    if (d > reglas(m).corte) {
      m += 1;
      if (m > 12) [y, m] = [y + 1, 1];
    }

    const fecha = new Date(Date.UTC(y, m - 1, reglas(m).envio));
    if (fecha.getUTCDay() === 0) fecha.setUTCDate(fecha.getUTCDate() + 1);
    return fecha;
  }
}

if (!customElements.get('bt-ship-date')) {
  customElements.define('bt-ship-date', BtShipDate);
}
